import { Trans, useTranslation } from 'next-i18next'
// Dependencies.
import styled from 'styled-components'
import { useRouter } from 'next/router'
import { useMemo, useState, useEffect, useContext } from 'react'

import { EventHookInterface, User, Option } from '../../../types'
import { P } from '../../P'
import { ParticipantProfile } from '../../ParticipantProfile'
import { SectionField } from '../../Inputs/SectionField'
import { H4 } from '../../H4'
import { UserContext } from '../../../context'
import { EmailInvite } from '../EmailInvite'

// Div.
const Div = styled.div`
  .stats {
    text-align: left;
  }

  .participants {
    display: flex;
    flex-wrap: wrap;
  }

  .addPeople {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 60px;

    & h4 {
      margin-bottom: 20px;
    }

    & .listItem {
      width: 100%;
    }
  }
`

// Props.
interface Props {
  eventHook: EventHookInterface
}

// Participants.
export const Participants: React.FC<Props> = (props) => {
  // Router.
  const router = useRouter()

  const hideCompWhen =
    router.pathname === '/events/create' ||
    router.pathname === '/events/duplicate/[eventId]'
  if (hideCompWhen === true) {
    return null
  } else {
    return <ParticipantsInner {...props} />
  }
}

export const ParticipantsInner: React.FC<Props> = (props) => {
  const { t } = useTranslation('common')

  // Props.
  const { eventHook } = props
  const { isEditable, value: event, isInside24hrs } = eventHook
  const { socialContacts } = useContext(UserContext)

  // State
  const [contactOption, setContactOption] = useState<Option[]>([])

  // Host Ids.
  // const hostIds = useMemo(() => event.host.reduce((acc, host) => {
  //   acc[host.id] = true
  //   return acc
  // }, {}), [event.host])

  // Participants.
  const participants = useMemo(() => {
    // Try to put host at the first place
    let hostUsers: User[] = [],
      attendingUsers: User[] = [],
      users: User[] = []
    if (event.participants && event.participants.length) {
      hostUsers = event.participants.filter((p) => event.host.id === p.id)
      attendingUsers = event.participants.filter(
        (p) => !hostUsers.some((h) => h.id === p.id),
      )
      users = users.concat(hostUsers).concat(attendingUsers)
    }

    return users
  }, [event.participants])

  // Remove Participant
  const removeParticipant = (participantId: number) => {
    eventHook.removeParticipantFromEvent(participantId)
  }

  // Add more people.
  const whoToInvite = useMemo(() => {
    if (!event.invites || !event.invites.length)
      return [{ event_id: event.id, email: '' }] // Empty String Only.
    const lastItemIsEmptyString =
      event.invites[event.invites.length - 1].email === ''
    if (!lastItemIsEmptyString)
      return [...event.invites, { event_id: event.id, email: '' }]
    return [...event.invites]
  }, [event])

  // Add Item Add Index.
  const addItemAtIndex = (index: number) => {
    eventHook.updateFieldsWithPartial({
      invites: [
        ...whoToInvite.slice(0, index + 1),
        { event_id: event.id, email: '' },
        ...whoToInvite.slice(index + 1),
      ],
    })
  }

  // Handle Backspace At Index.
  const handleBackspaceAtIndex = (index: number) => {
    const invites = [...whoToInvite]
    const target = invites[index]
    if (!target.email) invites.splice(index, 1)
    eventHook.updateFieldsWithPartial({ invites })
  }

  // On Change At Index.
  const onSelectChangeAtIndex = (event: Option, index: number) => {
    const invites = [...whoToInvite]
    if (event) {
      invites[index].email = event.value
    } else {
      invites[index].email = ''
    }
    eventHook.updateFieldsWithPartial({ invites })
  }

  // On Change At Index.
  const onInputChangeAtIndex = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const invites = [...whoToInvite]
    invites[index].email = event.target.value
    eventHook.updateFieldsWithPartial({ invites })
  }

  // On Key Down At Index.
  const onKeyDownAtIndex = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    const backspaceWasPressed = event.keyCode === 8
    const enterKeyWasPressed = event.keyCode === 13
    if (backspaceWasPressed) return handleBackspaceAtIndex(index)
    if (enterKeyWasPressed) return addItemAtIndex(index)
  }

  const peopleAttending =
    (event.participants && event.participants.length - 1) || 0
  const spotsAvailable = Math.max(0, event.group_size - peopleAttending)

  useEffect(() => {
    if (socialContacts) {
      const options = socialContacts.map((contact) => {
        return {
          label: `${contact.name} (${contact.email})`,
          value: contact.email,
        }
      })
      setContactOption(options)
    }
  }, [socialContacts])

  // .. hideCompWhen return null - see above

  // ..

  return (
    <SectionField
      header={
        isEditable
          ? t('Manage Participants', 'Manage Participants')
          : t('Participants', 'Participants')
      }
      subheader={
        isEditable
          ? t(
              'An invite email will be sent to these specific people but we’ll also provide a link for you to share',
              'An invite email will be sent to these specific people but we’ll also provide a link for you to share',
            )
          : ''
      }>
      <Div>
        {/* Stats. */}
        <div className="stats">
          {/* People Attending. */}
          <P>
            <Trans
              i18nKey="Participants_20220106162743344925051"
              count={peopleAttending}>
              {{ peopleAttending }} person is attending
              {/* plural {{ peopleAttending }} person are attending */}
            </Trans>
          </P>

          {/* Spots Available. */}
          <P>
            <Trans i18nKey="Participants_20220106162122660411445">
              {{ spotsAvailable }} spots available
            </Trans>
          </P>
        </div>

        {/* Participants. */}
        <div className="participants">
          {participants.map((participant) => {
            const { id } = participant
            const isHost = participant.id === event.host.id
            let image = participant.image
            if (!image) {
              image = '/images/avatar.png'
            }

            // ..
            return (
              <ParticipantProfile
                isHost={isHost}
                isEditable={isEditable}
                isInside24hrs={isInside24hrs()}
                isFreeEvent={event.event_paid_type === 0}
                key={id}
                {...participant}
                image={image}
                onRemove={() => removeParticipant(id)}
              />
            )
          })}
        </div>

        {/*Add People*/}
        {isEditable && (
          <div className="addPeople">
            <H4>{t(`Add people`, `Add people`)}</H4>

            {/* Added Item. */}
            <EmailInvite eventHook={eventHook}></EmailInvite>
          </div>
        )}
      </Div>
    </SectionField>
  )
}
